import BigNumMath from "./BigNumMath";
import ManExp from "./ManExp";


const WORKING = new ManExp(0, 0); // This is OK since JS is single-threaded.

export default class BigNum extends ManExp {
    /**
     * @param {number} value 
     */
    constructor(value) {
        super(0, 0);
        this.setValue(value);
    }

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    setValue(value) {
        BigNumMath.fromNumber(this, value);
        return this;
    }

    ///////////////////////////////////////////
    // Multiplication
    ///////////////////////////////////////////

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    multiplyNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.multiplyNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return BigNumMath.multiply(this, WORKING);
    }

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    mulNumber(value) {
        return this.multiplyNumber(value);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    multiply(value) {
        return BigNumMath.multiply(this, value);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    mul(value) {
        return this.multiply(value);
    }

    ///////////////////////////////////////////
    // Addition
    ///////////////////////////////////////////

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    addNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.addNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return BigNumMath.add(this, WORKING);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    add(value) {
        return BigNumMath.add(this, value);
    }

    ///////////////////////////////////////////
    // Subtraction
    ///////////////////////////////////////////

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    subNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.subNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, -value);
        return BigNumMath.add(this, WORKING);
    }

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    subtractNumber(value) {
        return this.subNumber(value);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    sub(value) {
        BigNumMath.copy(WORKING, value);
        BigNumMath.negate(WORKING);
        return BigNumMath.add(this, WORKING);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    subtract(value) {
        return this.sub(value);
    }

    ///////////////////////////////////////////
    // Division
    ///////////////////////////////////////////

    /**
     * Updates the number to its reciprocal.
     */
    recip() {
        return BigNumMath.recip(this);
    }

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    divideNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.divideNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        BigNumMath.recip(WORKING);
        return BigNumMath.multiply(this, WORKING);
    }

    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    divNumber(value) {
        return this.divideNumber(value);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    divide(value) {
        BigNumMath.copy(WORKING, value);
        BigNumMath.recip(WORKING);
        return BigNumMath.multiply(this, WORKING);
    }

    /**
     * @param {BigNum} value 
     * @returns {BigNum}
     */
    div(value) {
        return this.divide(value);
    }

    ///////////////////////////////////////////
    // Round/Floor/Ceil/Trunc
    ///////////////////////////////////////////    

    /**
     * @returns {BigNum}
     */
    round() {
        return BigNumMath.round(this);
    }

    /**
     * @returns {BigNum}
     */
    floor() {
        return BigNumMath.floor(this);
    }

    /**
     * @returns {BigNum}
     */
    ceil() {
        return BigNumMath.ceil(this);
    }

    /**
     * @returns {BigNum}
     */
    trunc() {
        return BigNumMath.trunc(this);
    }

    /**
     * @returns {BigNum}
     */
    abs() {
        return BigNumMath.abs(this);
    }

    ///////////////////////////////////////////
    // pow and log
    ///////////////////////////////////////////

    /**
     * @returns {number}
     */
    log10() {
        return BigNumMath.log10(this); // this is not modified
    }
  
    /**
     * @returns {number}
     */
    log2() {
        return BigNumMath.log2(this); // this is not modified
    }
  
    /**
     * @returns {number}
     */
    ln() {
        return BigNumMath.ln(this); // this is not modified
    }
  
    /**
     * @param {number} base 
     * @returns {number}
     */
    log(base) {
        return BigNumMath.log(this, base); // this is not modified
    }
  
    /**
     * @param {number} value 
     * @returns {BigNum}
     */
    pow(value) {
        return BigNumMath.pow(this, value);
    }
  

    ///////////////////////////////////////////
    // Conversions
    ///////////////////////////////////////////

    /**
     * @returns {string}
     */
    toString() {
        return BigNumMath.toString(this);
    }

    /**
     * @returns {string}
     */
    toDebugString() {
        return this.toString() + ', (m=' + this.m + ', e=' + this.e + ')';
    }

    /**
     * @returns {number}
     */
    toNumber() {
        return BigNumMath.toNumber(this);
    }

    /**
     * @param {number} places
     * @returns {string}
     */
    toExponential(places) {
        return BigNumMath.toExponential(this, places);
    }

    /**
     * @param {number} places
     * @returns {string}
     */
    toFixed(places) {
        return BigNumMath.toFixed(this, places);
    }

    /**
     * @param {number} places
     * @returns {string}
     */
    toPrecision(places) {
        return BigNumMath.toPrecision(this, places);
    }

    /**
     * @returns {string}
     */
    valueOf() {
        return BigNumMath.valueOf(this);
    }

    /**
     * @returns {string}
     */
    toJSON() {
        return BigNumMath.toJSON(this);
    }

    /**
     * @param {number} places
     * @returns {string}
     */
    toStringWithDecimalPlaces(places) {
        return BigNumMath.toStringWithDecimalPlaces(this, places);
    }

    /**
     * @returns {number}
     */
    sign() {
        return BigNumMath.sign(this);
    }

    ///////////////////////////////////////////
    // Comparisons
    ///////////////////////////////////////////

    /**
     * @param {number} value 
     * @returns {boolean}
     */
    equalsNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.equalsNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return this.equals(WORKING);
    }

    /**
     * @param {number} value 
     * @returns {boolean}
     */
    lessThanNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.lessThanNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return this.lessThan(WORKING);
    }

    /**
     * @returns {boolean}
     */
    lessThanZero() {
        WORKING.setZero();
        return this.lessThan(WORKING);
    }

    /**
     * @returns {boolean}
     */
    lessThanEqualsZero() {
        WORKING.setZero();
        return !this.greaterThan(WORKING);
    }

    /**
     * @param {number} value 
     * @returns {boolean}
     */
    lessThanEqualsNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.lessThanEqualsNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return this.lessThanEquals(WORKING);
    }

    /**
     * @param {number} value 
     * @returns {boolean}
     */
    greaterThanNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.greaterThanNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return this.greaterThan(WORKING);
    }

    /**
     * @param {number} value 
     * @returns {boolean}
     */
    greaterThanEqualsNumber(value) {
        if (value instanceof BigNum) {
            console.log('BigNum.greaterThanEqualsNumber() passed BigNum instead of number');
            console.trace();
        }

        BigNumMath.fromNumber(WORKING, value);
        return !this.lessThan(WORKING);
    }

    /**
     * @returns {boolean}
     */
    equalsZero() {
        WORKING.setZero();
        return this.equals(WORKING);
    }

    /**
     * @returns {boolean}
     */
    greaterThanZero() {
        WORKING.setZero();
        return this.greaterThan(WORKING);
    }

    /**
     * @returns {boolean}
     */
    greaterThanEqualsZero() {
        WORKING.setZero();
        return !this.lessThan(WORKING);
    }

}